import classNames from 'classnames';
import React, {ReactElement, useMemo} from 'react';

import {CelebrationIcon, MoneyIcon, ProductionIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {useGameStatus} from '../hooks';
import {computeHarvestEarnings, computeTonnesHarvested, formatMoney, isHarvestYear} from '../mechanics';
import ArrowButton from './buttons/ArrowButton';
import InfoBox from './InfoBox';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';

type Props = {
  onContinue: () => void;
};

const Harvest = ({onContinue}: Props): ReactElement => {
  const gameStatus = useGameStatus();
  const {isMobile} = useBreakpoint('mobile');

  const goalCompleted = useMemo(
    () => gameStatus.totalProduction >= gameStatus.productionGoal,
    [gameStatus.productionGoal, gameStatus.totalProduction]
  );
  const currentPeriodProduction = isHarvestYear(gameStatus) ? computeTonnesHarvested(gameStatus) : 0;
  const currentPeriodEarnings = isHarvestYear(gameStatus) ? computeHarvestEarnings(gameStatus) : 0;

  const isLastYear = useMemo(
    () => gameStatus.currentYear === gameStatus.totalYears - 1,
    [gameStatus.currentYear, gameStatus.totalYears]
  );

  return (
    <MessageBox
      top={<SimplePill>Harvest</SimplePill>}
      bottom={
        <div className="flex justify-center">
          <ArrowButton direction="right" onClick={onContinue}>
            {isLastYear ? 'Check your results' : `Go to year ${gameStatus.currentYear + 2}`}
          </ArrowButton>
        </div>
      }
    >
      <Col className={'pt-md px-xl mobile:p-sm text-center space-y-sm'}>
        <CelebrationIcon className="h-40 w-40 m-auto" />
        <p className="text-3xl font-semibold mobile:text-2xl">Well done!</p>
        <InfoBox className={classNames('text-start text-2xl font-semibold mobile:text-lg')}>
          <Col className="space-y-sm mobile:space-y-[4rem] mobile:pt-8 mobile:pb-4">
            <div
              className={classNames(
                'flex items-center',
                isMobile ? 'flex-col space-y-sm text-center' : 'flex-row space-x-md'
              )}
            >
              <div className="min-w-[9rem]">
                <ProductionIcon className="h-28 w-28 m-auto" />
              </div>

              <Col className="">
                <div>
                  Your land produced{' '}
                  <span className="text-primary-500">{currentPeriodProduction.toFixed(2)} tonnes this period</span>.
                </div>
                <div>
                  {goalCompleted ? (
                    <span>
                      That&apos;s already over your goal of{' '}
                      <span className="text-primary-500">{gameStatus.productionGoal} tonnes</span>. Let&apos;s see how
                      far you can get
                    </span>
                  ) : (
                    <span>
                      You are closer to your goal of{' '}
                      <span className="text-primary-500">{gameStatus.productionGoal} tonnes</span>.
                    </span>
                  )}
                </div>
              </Col>
            </div>
            <div
              className={classNames(
                'flex items-center',
                isMobile ? 'flex-col space-y-sm text-center' : 'flex-row space-x-md'
              )}
            >
              <div className="min-w-[9rem]">
                <MoneyIcon className="h-28 w-28 m-auto" />
              </div>
              <div>
                Additionally, by selling your production you earned{' '}
                <span className={classNames('whitespace-nowrap text-primary-500')}>
                  {formatMoney(currentPeriodEarnings)}
                </span>{' '}
                this period.
              </div>
            </div>
          </Col>
        </InfoBox>
      </Col>
    </MessageBox>
  );
};

export default Harvest;
